import type { SandboxImage, SandboxProfile } from '../types/cowork';

/** Platform profile IDs are cached as `p-profile-<id>` while images may keep the numeric ID. */
export const sandboxProfileAliases = (id: unknown): string[] => {
  const key = String(id ?? '');
  const matched = /^p-profile-(\d+)$/.exec(key);
  return matched ? [key, matched[1]] : [key];
};

/** Template owner profile ID; empty means the template is shared. */
const imageOwner = (image: SandboxImage): string =>
  image.e2bProfileId ? String(image.e2bProfileId) : '';

/** Return whether a cloud template can be used by the selected profile. */
export const isSandboxImageCompatible = (
  _profile: SandboxProfile | null | undefined,
  image: SandboxImage,
): boolean => Boolean(image.e2bTemplateId?.trim());

const byFavoriteThenOrder = <T extends { isFavorite?: boolean; sortOrder: number }>(a: T, b: T): number => {
  const favA = a.isFavorite === true;
  const favB = b.isFavorite === true;
  if (favA !== favB) return favA ? -1 : 1;
  return a.sortOrder - b.sortOrder;
};

/** 已启用的运行环境，收藏优先。 */
export const listEnabledSandboxProfiles = (profiles: SandboxProfile[]): SandboxProfile[] =>
  profiles.filter((profile) => profile.isEnabled !== false).slice().sort(byFavoriteThenOrder);

/** 某个运行环境下可用的模板：归属它的或通用的，且后端类型匹配。 */
export const listSandboxImagesForProfile = (
  profile: SandboxProfile,
  images: SandboxImage[],
): SandboxImage[] => {
  const aliases = sandboxProfileAliases(profile.id);
  return images
    // 只列启用的模板。停用是运营侧的明确动作——那个模板现在不许拿来开沙箱，
    // 所以任何选择器里都不该出现它。接口给的 isEnabled 恒为布尔值，故严格比对。
    .filter((image) => image.isEnabled === true)
    .filter((image) => {
      const owner = imageOwner(image);
      return !owner || aliases.includes(owner);
    })
    .filter((image) => isSandboxImageCompatible(profile, image))
    .slice()
    .sort(byFavoriteThenOrder);
};

/** 「选择执行环境」里云端沙箱那一组的一个可选项。 */
export interface CloudEnvOption {
  /** 选项唯一键：`<profileId>:<imageId>`。 */
  key: string;
  profileId: string;
  /** null = 该运行环境下没有任何模板，用后端默认镜像。 */
  imageId: string | null;
  label: string;
  description: string;
  /** 只有存在多个运行环境时才给出，用作分组小标题。 */
  groupLabel?: string;
}

export const cloudEnvOptionKey = (profileId: string | null, imageId: string | null): string =>
  `${profileId ?? ''}:${imageId ?? ''}`;

/**
 * 把运行环境 × 模板摊平成一层可选项。
 *
 * 常见部署只有一个运行环境，摊平后就是一串模板名，正是要的形状。存在多个运行环境时
 * 保留分组小标题，否则同名模板会重复出现且无从区分。
 */
export const buildCloudEnvOptions = (
  profiles: SandboxProfile[],
  images: SandboxImage[],
): CloudEnvOption[] => {
  const enabled = listEnabledSandboxProfiles(profiles);
  const multiProfile = enabled.length > 1;
  const options: CloudEnvOption[] = [];
  for (const profile of enabled) {
    const usable = listSandboxImagesForProfile(profile, images);
    if (usable.length === 0) {
      options.push({
        key: cloudEnvOptionKey(profile.id, null),
        profileId: profile.id,
        imageId: null,
        label: profile.name,
        description: profile.description ?? '',
      });
      continue;
    }
    for (const image of usable) {
      options.push({
        key: cloudEnvOptionKey(profile.id, image.id),
        profileId: profile.id,
        imageId: image.id,
        label: image.name || image.imageName,
        description: image.description ?? '',
        groupLabel: multiProfile ? profile.name : undefined,
      });
    }
  }
  return options;
};

/**
 * 当前 (profileId, imageId) 命中的选项。
 *
 * 历史数据可能只存了模板没存运行环境，或反过来，所以精确匹配之外还要按单边回退，
 * 否则编辑一个老任务会显示成「未选择」，一保存就把原来的选择丢了。
 */
export const findCloudEnvOption = (
  options: CloudEnvOption[],
  profileId: string | null | undefined,
  imageId: string | null | undefined,
): CloudEnvOption | null => {
  const wantedProfile = profileId ? String(profileId) : '';
  const wantedImage = imageId ? String(imageId) : '';
  if (wantedProfile && wantedImage) {
    const exact = options.find((o) => o.profileId === wantedProfile && String(o.imageId ?? '') === wantedImage);
    if (exact) return exact;
  }
  if (wantedImage) {
    const byImage = options.find((o) => String(o.imageId ?? '') === wantedImage);
    if (byImage) return byImage;
  }
  if (wantedProfile) {
    const byProfile = options.find((o) => o.profileId === wantedProfile);
    if (byProfile) return byProfile;
  }
  return null;
};

/**
 * 已有会话的执行环境：会话一旦带着 (profileId, imageId) 建立，就只认它自己的值。
 * CoworkView 里的覆盖值是给「还没定环境的会话」用的，残留下来时不能盖掉会话自己的模板。
 */
export const resolveSessionSandboxSelection = (
  session: { sandboxProfileId?: string | null; sandboxImageId?: string | null } | null | undefined,
  override: { profileId?: string | null; imageId?: string | null },
): { profileId: string | null; imageId: string | null; locked: boolean } => {
  const sessionProfile = session?.sandboxProfileId ?? null;
  const sessionImage = session?.sandboxImageId ?? null;
  if (sessionProfile && sessionImage) {
    return { profileId: sessionProfile, imageId: sessionImage, locked: true };
  }
  return {
    profileId: override.profileId ?? sessionProfile,
    imageId: override.imageId ?? sessionImage,
    locked: false,
  };
};

/**
 * 已锁定会话的 chip 显示：只按会话自己的模板匹配，不做 findCloudEnvOption 那种「按运行环境回退」——
 * 那会把已删除/停用的模板显示成同组第一个模板（例如 721-Sandbox），让人误以为会话换了环境。
 * 命中不了时返回 unavailable，由调用方显示「不可用」。
 */
export const resolveSessionCloudEnv = (
  options: CloudEnvOption[],
  profileId: string | null | undefined,
  imageId: string | null | undefined,
): { option: CloudEnvOption | null; label: string | null; unavailable: boolean } => {
  const wantedProfile = profileId ? String(profileId) : '';
  const wantedImage = imageId ? String(imageId) : '';
  if (!wantedImage) {
    const byProfile = wantedProfile
      ? options.find((o) => o.profileId === wantedProfile && o.imageId === null)
      : undefined;
    return byProfile
      ? { option: byProfile, label: byProfile.label, unavailable: false }
      : { option: null, label: null, unavailable: Boolean(wantedProfile) };
  }
  const match = options.find((o) => o.profileId === wantedProfile && String(o.imageId ?? '') === wantedImage)
    ?? options.find((o) => String(o.imageId ?? '') === wantedImage);
  if (match) return { option: match, label: match.label, unavailable: false };
  return { option: null, label: null, unavailable: true };
};
